// src/services/azureProjectInvoicedPeriodsService.js
// Azure Table Storage Service für Versendet-Zeiträume (Invoiced Periods) pro Projekt

const AZURE_CONFIG = {
  storageAccount: "mfprojects",
  tableName: "projectInvoicedPeriods",
  sasToken:
    "sv=2024-11-04&ss=t&srt=sco&sp=rwdlacu&se=2035-07-04T17:08:25Z&st=2025-07-04T09:08:25Z&spr=https&sig=X7s%2BFIyYcKvRzSxRNAp5BUJEe2YUcg8nojkn9fIfcTY%3D",
};

// Flag to track if table creation was attempted
let tableCreationAttempted = false;

// Create the table if it doesn't exist
const ensureTableExists = async () => {
  if (tableCreationAttempted) return;

  try {
    const url = `https://${AZURE_CONFIG.storageAccount}.table.core.windows.net/Tables?${AZURE_CONFIG.sasToken}`;

    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json;odata=nometadata",
      },
      body: JSON.stringify({
        TableName: AZURE_CONFIG.tableName,
      }),
    });

    if (response.ok) {
      console.log(`✅ Table '${AZURE_CONFIG.tableName}' created successfully`);
    } else if (response.status === 409) {
      // Table already exists - this is fine
      console.log(`✅ Table '${AZURE_CONFIG.tableName}' already exists`);
    } else {
      const errorText = await response.text();
      console.warn(`⚠️ Could not create table: ${errorText}`);
    }
  } catch (error) {
    console.warn(`⚠️ Table creation failed: ${error.message}`);
  } finally {
    tableCreationAttempted = true;
  }
};

// Hilfsfunktion für RowKey
const createRowKey = (projectCode, timestamp) => {
  return `${projectCode}_${timestamp || Date.now()}`;
};

// Alle versendeten Zeiträume für ein Projekt laden
export const getInvoicedPeriods = async (projectCode) => {
  // Ensure table exists before trying to query
  await ensureTableExists();

  try {
    let allPeriods = [];
    let nextPartitionKey = null;
    let nextRowKey = null;
    const filter = `$filter=ProjectCode eq '${projectCode}'`;

    do {
      let url = `https://${AZURE_CONFIG.storageAccount}.table.core.windows.net/${AZURE_CONFIG.tableName}?${AZURE_CONFIG.sasToken}&${filter}`;

      if (nextPartitionKey && nextRowKey) {
        url += `&NextPartitionKey=${encodeURIComponent(nextPartitionKey)}`;
        url += `&NextRowKey=${encodeURIComponent(nextRowKey)}`;
      }

      const response = await fetch(url, {
        headers: { Accept: "application/json;odata=nometadata" },
      });

      if (!response.ok) {
        // If table not found (404), return empty array
        if (response.status === 404) {
          console.log(`Table '${AZURE_CONFIG.tableName}' not found, returning empty results`);
          return [];
        }

        const errorText = await response.text();
        console.error("Azure Error:", errorText);
        throw new Error(`Azure Table Error: ${response.status}`);
      }

      const data = await response.json();
      allPeriods = allPeriods.concat(data.value || []);

      nextPartitionKey = response.headers.get(
        "x-ms-continuation-NextPartitionKey"
      );
      nextRowKey = response.headers.get("x-ms-continuation-NextRowKey");
    } while (nextPartitionKey && nextRowKey);

    const periods = allPeriods.map((item) => ({
      rowKey: item.RowKey,
      projectCode: item.ProjectCode,
      dateFrom: item.DateFrom,
      dateTo: item.DateTo,
      totalHours: item.TotalHours || 0,
      totalCHF: item.TotalCHF || 0,
      invoiceReference: item.InvoiceReference || "",
      createdAt: item.CreatedAt,
      updatedAt: item.UpdatedAt,
      createdBy: item.CreatedBy || "",
      linkedBudgetEntryId: item.LinkedBudgetEntryId || "",
    }));

    // Sortiere nach Datum (neueste zuerst)
    periods.sort((a, b) => b.dateFrom.localeCompare(a.dateFrom));

    return periods;
  } catch (error) {
    console.error("Invoiced Periods Load Error:", error);
    return [];
  }
};

// Neuen versendeten Zeitraum speichern
export const saveInvoicedPeriod = async (projectCode, periodData) => {
  // Ensure table exists before trying to save
  await ensureTableExists();

  try {
    const timestamp = Date.now();
    const rowKey = createRowKey(projectCode, timestamp);
    const now = new Date().toISOString();

    const url = `https://${AZURE_CONFIG.storageAccount}.table.core.windows.net/${AZURE_CONFIG.tableName}(PartitionKey='invoiced',RowKey='${rowKey}')?${AZURE_CONFIG.sasToken}`;

    const entity = {
      PartitionKey: "invoiced",
      RowKey: rowKey,
      ProjectCode: projectCode,
      DateFrom: periodData.dateFrom,
      DateTo: periodData.dateTo,
      TotalHours: periodData.totalHours || 0,
      TotalCHF: periodData.totalCHF || 0,
      InvoiceReference: periodData.invoiceReference || "",
      CreatedAt: now,
      UpdatedAt: now,
      CreatedBy: periodData.createdBy || "",
      LinkedBudgetEntryId: periodData.linkedBudgetEntryId || "",
    };

    const response = await fetch(url, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json;odata=nometadata",
      },
      body: JSON.stringify(entity),
    });

    if (!response.ok) {
      // If table not found, try to create it and retry once
      if (response.status === 404 && !tableCreationAttempted) {
        console.log("Table not found during save, creating and retrying...");
        tableCreationAttempted = false; // Reset flag to allow creation
        await ensureTableExists();

        // Retry the save operation
        const retryResponse = await fetch(url, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json;odata=nometadata",
          },
          body: JSON.stringify(entity),
        });

        if (!retryResponse.ok) {
          const retryErrorText = await retryResponse.text();
          console.error("Azure Save Error (retry):", retryErrorText);
          throw new Error(`Azure Save Error (retry): ${retryResponse.status}`);
        }

        return {
          success: true,
          rowKey: rowKey,
        };
      }

      const errorText = await response.text();
      console.error("Azure Save Error:", errorText);
      throw new Error(`Azure Save Error: ${response.status}`);
    }

    return {
      success: true,
      rowKey: rowKey,
    };
  } catch (error) {
    console.error("Save Invoiced Period Error:", error);
    throw error;
  }
};

// Versendeten Zeitraum löschen
export const deleteInvoicedPeriod = async (projectCode, rowKey) => {
  try {
    const url = `https://${AZURE_CONFIG.storageAccount}.table.core.windows.net/${AZURE_CONFIG.tableName}(PartitionKey='invoiced',RowKey='${rowKey}')?${AZURE_CONFIG.sasToken}`;

    const response = await fetch(url, {
      method: "DELETE",
      headers: {
        "If-Match": "*", // Löscht ohne Etag-Prüfung
      },
    });

    if (!response.ok && response.status !== 404) {
      const errorText = await response.text();
      console.error("Azure Delete Error:", errorText);
      throw new Error(`Azure Delete Error: ${response.status}`);
    }

    return true;
  } catch (error) {
    console.error("Delete Invoiced Period Error:", error);
    throw error;
  }
};

// Prüfen ob ein Zeiteintrag in einem versendeten Zeitraum liegt
export const isTimeEntryInvoiced = (entryDate, invoicedPeriods) => {
  if (!entryDate || !invoicedPeriods || invoicedPeriods.length === 0) {
    return false;
  }

  return invoicedPeriods.some((period) => {
    return entryDate >= period.dateFrom && entryDate <= period.dateTo;
  });
};

// Prüfen ob sich zwei Zeiträume überschneiden
export const hasOverlappingPeriods = (newDateFrom, newDateTo, existingPeriods) => {
  return existingPeriods.filter((period) => {
    // Zeiträume überschneiden sich wenn:
    // newStart <= existingEnd UND newEnd >= existingStart
    return newDateFrom <= period.dateTo && newDateTo >= period.dateFrom;
  });
};

// Konfiguration aktualisieren (für andere SAS Token etc.)
export const updateConfig = (newConfig) => {
  Object.assign(AZURE_CONFIG, newConfig);
};
